import {
  addMonths,
  differenceInMonths,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  lastDayOfYear,
  setYear,
  startOfYear,
} from 'date-fns';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {Nullish, parseDate} from 'shared/web';

import {MIN_MANUFACTURE_YEAR} from '../constants/MIN_MANUFACTURE_YEAR';
import {
  NL_BPM_DEPRECIATION_BRACKETS,
  NL_BPM_DIESEL_SURCHARGE_ABOVE_CO2,
  NL_BPM_DIESEL_SURCHARGE_BASE_CO2,
  NL_BPM_DIESEL_SURCHARGE_PER_GRAM,
  NL_BPM_ESTIMATE_TOLERANCE,
  NL_BPM_FIXED_RATE,
  NL_BPM_MAX_DEPRECIATION_PERCENT,
  NL_BPM_NEW_VEHICLE_MAX_AGE_MONTHS,
  NL_BPM_NEW_VEHICLE_MILEAGE_LIMIT,
  NL_BPM_TARIFF_BRACKETS,
  NL_BPM_TARIFF_YEAR,
  NL_BPM_WLTP_MANDATORY_FROM,
} from '../constants/nlBpmTariff';

export type NlBpmUnavailableReason = 'NO_CO2' | 'NO_REGISTRATION_DATE' | 'VAN';

export interface NlBpmBreakdown {
  grossBpm: number;
  depreciationPercent: number;
  ageMonths: number;
}

/** The date the car's age is counted from. */
export type NlBpmRegistrationBasis =
  | {source: 'firstRegistration'; date: Date}
  /**
   * The first registration is missing: 31 December of the manufacture year,
   * never later than today. Only an indicative basis, and the card has to say so.
   */
  | {source: 'manufactureYear'; date: Date; manufactureYear: number};

export type NlBpmEstimate =
  | {status: 'unavailable'; reason: NlBpmUnavailableReason}
  | {
      status: 'computed';
      breakdown: NlBpmBreakdown;
      estimate: number;
      /** Null for a fully depreciated car: zero is what the table says, not an estimate. */
      rangeMin: number | null;
      rangeMax: number | null;
      isElectric: boolean;
      isEmissionCycleUncertain: boolean;
      isTariffStale: boolean;
      registrationBasis: NlBpmRegistrationBasis;
    }
  | {
      status: 'twoScenarios';
      breakdown: NlBpmBreakdown;
      asNew: number;
      asUsed: number;
      isElectric: boolean;
      isEmissionCycleUncertain: boolean;
      isTariffStale: boolean;
      registrationBasis: NlBpmRegistrationBasis;
    };

export interface NlBpmInput {
  co2: number | Nullish;
  /**
   * Date of first registration, ISO format. Ad sources almost always publish
   * only the month and year, so the day is padded to the first of the month.
   * The depreciation table counts started months, which means a car can land
   * up to one month too far down the table.
   */
  registrationDate: string | Nullish;
  /**
   * Year of manufacture, used only when the first registration is missing.
   * The age is then counted from 31 December of that year, the youngest the
   * car can be, so the estimate is the pessimistic one: the least depreciation
   * and therefore the highest BPM.
   */
  manufactureYear: number | Nullish;
  /** Fuel type key from the codelist, for example FUELTYPE_DIESEL. */
  fuelTypeKey: string | Nullish;
  /** Body style key from the codelist; VANSTYLE_* means a van. */
  carStyleKey: string | Nullish;
  mileage: number | Nullish;
  /** Date the estimate is calculated for. Defaults to today. */
  today?: Date;
}

const isDiesel = (fuelTypeKey: string | Nullish) => Boolean(fuelTypeKey?.includes('DIESEL'));

const isElectricFuel = (fuelTypeKey: string | Nullish) =>
  Boolean(fuelTypeKey?.includes('ELECTRIC'));

const isVanBodyStyle = (carStyleKey: string | Nullish) =>
  Boolean(carStyleKey?.startsWith('VANSTYLE'));

const parseRegistrationDate = (registrationDate: string | Nullish) => {
  // parseDate also returns null for the empty string some sources send instead of a date.
  const parsed = isNil(registrationDate) ? null : parseDate(registrationDate);

  return isNotNil(parsed) && isValid(parsed) ? parsed : null;
};

/**
 * The first registration when there is one. Without it the manufacture year is
 * better than nothing. 31 December is the youngest the car can be, which gives
 * the pessimistic, higher BPM - a buyer would rather be surprised by a lower
 * tax than a higher one. A model year ahead of the calendar must not date the
 * car into the future.
 */
const getRegistrationBasis = (
  input: Pick<NlBpmInput, 'registrationDate' | 'manufactureYear'>,
  today: Date
): NlBpmRegistrationBasis | null => {
  const firstRegistration = parseRegistrationDate(input.registrationDate);

  if (isNotNil(firstRegistration)) {
    return {source: 'firstRegistration', date: firstRegistration};
  }

  const manufactureYear = input.manufactureYear;

  if (
    isNil(manufactureYear) ||
    !Number.isInteger(manufactureYear) ||
    manufactureYear < MIN_MANUFACTURE_YEAR
  ) {
    return null;
  }

  const yearEnd = lastDayOfYear(setYear(today, manufactureYear));

  return {
    source: 'manufactureYear',
    manufactureYear,
    date: isAfter(yearEnd, today) ? today : yearEnd,
  };
};

/** Gross BPM from the tariff table, before depreciation. */
export function getGrossBpm(co2: number, fuelTypeKey: string | Nullish): number {
  const bracket = NL_BPM_TARIFF_BRACKETS.reduce((selected, candidate) =>
    co2 >= candidate.fromCo2 ? candidate : selected
  );

  const tariff = bracket.baseAmount + (co2 - bracket.fromCo2) * bracket.ratePerGram;

  const dieselSurcharge =
    isDiesel(fuelTypeKey) && co2 > NL_BPM_DIESEL_SURCHARGE_ABOVE_CO2
      ? (co2 - NL_BPM_DIESEL_SURCHARGE_BASE_CO2) * NL_BPM_DIESEL_SURCHARGE_PER_GRAM
      : 0;

  return tariff + dieselSurcharge;
}

/**
 * Age in whole months plus a flag for a partially elapsed month, because the
 * depreciation table counts started months as well.
 */
function getVehicleAge(
  registrationDate: Date,
  today: Date
): {wholeMonths: number; hasStartedMonth: boolean} {
  const wholeMonths = Math.max(differenceInMonths(today, registrationDate), 0);

  return {
    wholeMonths,
    hasStartedMonth: !isSameDay(addMonths(registrationDate, wholeMonths), today),
  };
}

/** Flat-rate depreciation percentage for the car's age. */
export function getDepreciationPercent(registrationDate: Date, today: Date): number {
  const {wholeMonths, hasStartedMonth} = getVehicleAge(registrationDate, today);

  const bracket = NL_BPM_DEPRECIATION_BRACKETS.reduce((selected, candidate) =>
    wholeMonths >= candidate.fromMonths ? candidate : selected
  );

  const startedMonths = wholeMonths - bracket.fromMonths + (hasStartedMonth ? 1 : 0);
  const percent = bracket.basePercent + startedMonths * bracket.percentPerMonth;

  return Math.min(percent, NL_BPM_MAX_DEPRECIATION_PERCENT);
}

const applyDepreciation = (grossBpm: number, depreciationPercent: number) =>
  Math.round(grossBpm * (1 - depreciationPercent / 100));

/**
 * Estimates the Dutch BPM for an imported passenger car.
 *
 * TODO(T20-139199): this belongs on the backend, together with the tables.
 *
 * Uses the flat-rate depreciation and the current tariff. The historic tariff
 * under art. 10b is deliberately not handled, so older cars are more likely to
 * be overestimated.
 */
export function calculateNlBpm(input: NlBpmInput): NlBpmEstimate {
  if (isVanBodyStyle(input.carStyleKey)) {
    return {status: 'unavailable', reason: 'VAN'};
  }

  const today = input.today ?? new Date();

  const registrationBasis = getRegistrationBasis(input, today);

  if (isNil(registrationBasis)) {
    return {status: 'unavailable', reason: 'NO_REGISTRATION_DATE'};
  }

  const registrationDate = registrationBasis.date;

  const isElectric = isElectricFuel(input.fuelTypeKey);

  if (!isElectric && isNil(input.co2)) {
    return {status: 'unavailable', reason: 'NO_CO2'};
  }

  // An electric car always pays the fixed rate. Some sources still report a
  // non-zero CO2 for one, and following that number would contradict what the
  // card says about the amount.
  const grossBpm =
    isElectric || isNil(input.co2) ? NL_BPM_FIXED_RATE : getGrossBpm(input.co2, input.fuelTypeKey);
  const depreciationPercent = getDepreciationPercent(registrationDate, today);
  const {wholeMonths} = getVehicleAge(registrationDate, today);

  const breakdown: NlBpmBreakdown = {
    grossBpm: Math.round(grossBpm),
    depreciationPercent,
    ageMonths: wholeMonths,
  };

  // The cycle depends on the earliest date the car can have been registered.
  // For a manufacture year that is 1 January, not the 31 December the age is
  // counted from - otherwise a car built in 2020 would pass as WLTP-certain.
  const earliestRegistrationDate =
    registrationBasis.source === 'manufactureYear'
      ? startOfYear(registrationDate)
      : registrationDate;

  // Before WLTP became mandatory we cannot tell which cycle the CO₂ follows.
  // This applies to both computed results, not only to the single estimate.
  const isEmissionCycleUncertain =
    !isElectric && isBefore(earliestRegistrationDate, NL_BPM_WLTP_MANDATORY_FROM);

  // The rates are hand-maintained here, so at least notice when they are behind.
  const isTariffStale = today.getFullYear() > NL_BPM_TARIFF_YEAR;

  // Both the mileage and the age have to fit: a long-registered car with low
  // mileage is used, however little it has driven.
  const isNearlyNew =
    isNotNil(input.mileage) &&
    input.mileage < NL_BPM_NEW_VEHICLE_MILEAGE_LIMIT &&
    wholeMonths <= NL_BPM_NEW_VEHICLE_MAX_AGE_MONTHS;

  if (isNearlyNew) {
    return {
      status: 'twoScenarios',
      breakdown,
      asNew: Math.round(grossBpm),
      asUsed: applyDepreciation(grossBpm, depreciationPercent),
      isElectric,
      isEmissionCycleUncertain,
      isTariffStale,
      registrationBasis,
    };
  }

  const estimate = applyDepreciation(grossBpm, depreciationPercent);

  // At 100 % depreciation zero is what the law gives, not an estimate: neither
  // a range nor doubt about the emission cycle changes that.
  const isFullyDepreciated = estimate === 0;

  // The ±10 % range describes the gap against a koerslijst valuation. With an
  // unknown emission cycle or outdated rates we cannot back that precision, so
  // we promise no range at all. An age assumed from the manufacture year keeps
  // the range: the card already flags the estimate as pessimistic.
  const hasRange = !isFullyDepreciated && !isEmissionCycleUncertain && !isTariffStale;

  return {
    status: 'computed',
    breakdown,
    estimate,
    rangeMin: hasRange ? Math.round(estimate * (1 - NL_BPM_ESTIMATE_TOLERANCE)) : null,
    rangeMax: hasRange ? Math.round(estimate * (1 + NL_BPM_ESTIMATE_TOLERANCE)) : null,
    isElectric,
    isEmissionCycleUncertain: isEmissionCycleUncertain && !isFullyDepreciated,
    isTariffStale,
    registrationBasis,
  };
}
